// AI agentic payments. Three revenue paths over a single cost line.
//
//   1. Quota+overage:   tier.aiAgentRunsIncluded free runs, then overage rate
//   2. Outcome-based:   "Pulsar Save" agents, % of measured savings
//   3. Merchant referral: Y2+ partner-routed payment volume × ref rate
//
// Cost line: aiAgents.costPerRun × runs (default €0.001 — cheap-model routing).
//
// B2B agentic infrastructure (Portal upgrade fees, settlement fees, etc.) is
// a separate concern handled by inputs.agenticInfra (toggle). Schema bump
// pending; this module is consumer-side AI only.

/**
 * AI agentic line for a single tier in a single month, per active user.
 *
 * @param {object} inputs
 * @param {object} tier
 * @param {object} scenario
 * @param {string} tierKey   'free' | 'pro'
 * @returns {{
 *   runsAvg: number,        // runs per active user / month
 *   overageRev: number,     // EUR / active user / month (paid by user above quota)
 *   outcomeRev: number,     // EUR / active user / month (Pulsar Save outcome share, /12)
 *   referralRev: number,    // EUR / active user / month (merchant referral share)
 *   cost: number,           // EUR / active user / month (compute cost)
 * }}
 */
export function agenticPerUser(inputs, tier, scenario, tierKey) {
  if (inputs.aiAgents?.enabled === false) {
    return { runsAvg: 0, overageRev: 0, outcomeRev: 0, referralRev: 0, cost: 0 };
  }

  const runsPerDay = tierKey === 'free' ? scenario.freeAiActual : scenario.proAiActual;
  const runsPerMonth = runsPerDay * 30;
  const included = inputs.aiAgents.runsIncluded[tierKey];
  const overageRuns = Math.max(0, runsPerMonth - included);
  const overageRev = overageRuns * inputs.aiAgents.overageRate[tierKey];

  // Outcome-based: a fraction of agentic-active users opt into Pulsar Save
  // and Pulsar takes a share of the savings unlocked.
  const oc = inputs.aiAgents.outcomeBasedSavings;
  const outcomeRev = (oc.adoption * oc.avgPerYear * oc.pulsarShare) / 12;

  // Merchant referral: Y1 off, Y2+ ramps. Modeled as a flat per-active-user
  // contribution at steady state; forecast.js gates by month/year.
  const mr = inputs.aiAgents.merchantReferral;
  const referralRev = mr.y1Active === false ? 0 : mr.y2Volume * mr.refRate;

  // Compute cost: only paid runs (free runs have a cost too, modeled as runs
  // × costPerRun for the full count — Pulsar eats infra for free runs).
  const cost = runsPerMonth * inputs.aiAgents.costPerRun;

  return { runsAvg: runsPerMonth, overageRev, outcomeRev, referralRev, cost };
}
